import { useState, type FC, useEffect, type Dispatch, type SetStateAction, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type PreparePaymentRequest, preparePayment, type EwayAccessCode } from '@/api/registration/payment';
import { type ApiResponse } from '@/api/tenantClient';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import PayByEWay from './PayByEWay';
import { SkeletonText } from '@chakra-ui/react';
import { cartQueryKey, listOrdersQueryKey, registrationQueryKey } from '@/api/registration';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';

export interface PayByEwayWrapperProps {
  setErrors: Dispatch<SetStateAction<string[]>>
  isValid: boolean
}

const PayByEwayWrapper: FC<PayByEwayWrapperProps> = ({
  setErrors,
  isValid,
  ...props
}) => {
  const [options, setOptions] = useState<EwayAccessCode>();
  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();
  const { userSession } = useUserSession();
  const registrationQuery = useRegistrationQuery(eventId);

  const { data: cart } = useCartQuery(eventId);

  const { mutate: preparePaymentMutate, isLoading } = useMutation<ApiResponse<EwayAccessCode>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<EwayAccessCode>;
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        void queryClient.invalidateQueries({ queryKey: [registrationQuery] });
        void queryClient.invalidateQueries({ queryKey: [listOrdersQueryKey, { eventId, userSession }] });
        void queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
        setOptions(result.items[0]);
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  useEffect(() => {
    if (cart) {
      const preparePaymentData: PreparePaymentRequest = {
        cartId: cart.id,
        locale: 'aud',
        gateway: 'eWay'
      };
      setErrors([]);
      preparePaymentMutate(preparePaymentData);
    }
  }, [cart]);

  return isLoading ? (
    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
  ) : options?.accessCodesShared && (
    <PayByEWay
      setErrors={setErrors}
      isValid={isValid}
      options={options}
    />
  );
};

export default PayByEwayWrapper;
